import React, { useState, useEffect } from 'react';
import { MapPin, User, Check, Calendar } from 'lucide-react';
import { MainLayout } from './MainLayout';
import { useApp } from './AppContext';
import { formatDateDisplay } from '../utils/dateUtils';
import { useTranslation } from '../i18n/TranslationContext';

interface DateOption {
  id: string;
  date: string;
  displayDate: string;
  capacity: string;
  state: 'default' | 'selected-applied' | 'booked';
}

type ApplicationMode = 'display' | 'edit';

export function Apply() {
  const { updateProcessState, navigateWithProcessCheck, navigateDirectly, processState, getCurrentStep, userType, jobs } = useApp();
  const { t } = useTranslation();
  const [mode, setMode] = useState<ApplicationMode>('display');
  const [showFullDescription, setShowFullDescription] = useState(false);
  
  // Initialize dates based on user type
  const getInitialDateState = (): DateOption[] => {
    const allDates = [
      { id: '1', date: '2024-01-14', displayDate: '14 Jan', capacity: '3/12' },
      { id: '2', date: '2024-01-18', displayDate: '18 Jan', capacity: '5/12' },
      { id: '3', date: '2024-01-21', displayDate: '21 Jan', capacity: '2/12' },
      { id: '4', date: '2024-01-25', displayDate: '25 Jan', capacity: '7/12' },
      { id: '5', date: '2024-01-28', displayDate: '28 Jan', capacity: '4/12' },
      { id: '6', date: '2024-02-01', displayDate: '1 Feb', capacity: '6/12' },
      { id: '7', date: '2024-02-05', displayDate: '5 Feb', capacity: '3/12' },
      { id: '8', date: '2024-02-08', displayDate: '8 Feb', capacity: '8/12' },
      { id: '9', date: '2024-02-12', displayDate: '12 Feb', capacity: '2/12' },
      { id: '10', date: '2024-02-15', displayDate: '15 Feb', capacity: '5/12' },
      { id: '11', date: '2024-02-19', displayDate: '19 Feb', capacity: '4/12' },
      { id: '12', date: '2024-02-22', displayDate: '22 Feb', capacity: '7/12' },
      { id: '13', date: '2024-02-26', displayDate: '26 Feb', capacity: '3/12' },
    ];

    // For new signup users: clean state - all dates default
    if (userType === 'new_signup') {
      return allDates.map(date => ({ ...date, state: 'default' as const }));
    }
    
    // For existing login users: show existing applied/booked dates
    if (userType === 'existing_login' || userType === 'booked_user') {
      return allDates.map((date, index) => {
        // Mock existing user data - some applied and booked dates
        if ([1, 2, 4, 6].includes(index)) { // Applied dates
          return { ...date, state: 'selected-applied' as const };
        } else if ([7].includes(index)) { // Booked dates
          return { ...date, state: 'booked' as const };
        } else {
          return { ...date, state: 'default' as const };
        }
      });
    }

    // Default fallback
    return allDates.map(date => ({ ...date, state: 'default' as const }));
  };

  const [dates, setDates] = useState<DateOption[]>(getInitialDateState);

  // Store original selected-applied dates for cancel functionality
  const [originalSelectedAppliedDates, setOriginalSelectedAppliedDates] = useState<string[]>([]);

  // Constants for limits
  const MAX_TOTAL_SELECTABLE = 5;
  const MAX_BOOKED_DATES = 2;

  // Calculate counts
  const bookedCount = dates.filter(date => date.state === 'booked').length;
  const selectedAppliedCount = dates.filter(date => date.state === 'selected-applied').length;
  
  const totalSelectedCount = selectedAppliedCount;
  const maxSelectableCount = Math.max(0, MAX_TOTAL_SELECTABLE - bookedCount);
  const canSelectMore = totalSelectedCount < maxSelectableCount;
  const isMaxWorkLimitReached = bookedCount >= MAX_BOOKED_DATES;

  // Initialize dates based on user type when user type changes
  useEffect(() => {
    setDates(getInitialDateState());
  }, [userType]);

  // Auto-withdraw applications when max work limit is reached
  useEffect(() => {
    if (isMaxWorkLimitReached && mode === 'display') {
      setDates(prev =>
        prev.map(date =>
          date.state === 'selected-applied'
            ? { ...date, state: 'default' }
            : date
        )
      );
    }
  }, [bookedCount, isMaxWorkLimitReached, mode]);

  const handleDateClick = (dateId: string) => {
    // Allow direct clicking for new signup users in display mode
    const canClick = (mode === 'edit' && !isMaxWorkLimitReached) || 
                     (userType === 'new_signup' && mode === 'display' && !isMaxWorkLimitReached);
    
    if (canClick) {
      setDates(prev =>
        prev.map(date => {
          if (date.id === dateId) {
            // Handle state transitions based on current state
            switch (date.state) {
              case 'selected-applied':
                return { ...date, state: 'default' }; // Remove selected/applied date
              case 'default':
                // Only allow selection if under limit
                if (canSelectMore) {
                  return { ...date, state: 'selected-applied' }; // Select default date
                }
                return date; // Don't change if at limit
              case 'booked':
                return date; // Booked dates are not clickable
              default:
                return date;
            }
          }
          return date;
        })
      );
    }
  };

  const handleEnterEditMode = () => {
    if (!isMaxWorkLimitReached) {
      // Store original selected-applied dates for cancel functionality
      setOriginalSelectedAppliedDates(dates.filter(date => date.state === 'selected-applied').map(date => date.id));
      setMode('edit');
    }
  };

  // Get button text based on user type and current state
  const getButtonText = () => {
    if (mode === 'edit') {
      return 'Set Availability & Continue';
    }
    
    // For new signup users
    if (userType === 'new_signup') {
      return selectedAppliedCount > 0 ? 'Edit Availability' : 'Set Availability';
    }
    
    // For existing users
    const hasAnySelectedAppliedDates = selectedAppliedCount > 0;
    return hasAnySelectedAppliedDates ? 'Edit Availability' : 'Set Availability';
  };

  const [validationError, setValidationError] = useState<string>('');

  // Clear validation error when user makes selections
  useEffect(() => {
    if (totalSelectedCount > 0 && validationError) {
      setValidationError('');
    }
  }, [totalSelectedCount, validationError]);

  const handleSaveUpdate = () => {
    // Validate at least 1 date is selected for availability
    const selectedCount = dates.filter(date => date.state === 'selected-applied').length;
    
    if (selectedCount > 0) {
      // Clear any validation error
      setValidationError('');
      // All selected-applied dates remain as selected-applied (no state change needed)
      setMode('display');
      
      // Mark Step 1 complete with applied date and navigate to Step 2 (Financial Data)
      const appliedDate = formatDateDisplay(new Date());
      updateProcessState({ 
        availabilityCompleted: true,
        appliedDate: appliedDate
      });
      navigateDirectly('financial-data');
    } else {
      // Show validation error
      setValidationError('Bitte wählen Sie mindestens 1 verfügbaren Termin');
    }
  };

  // Handle immediate save for new signup users (no edit mode needed)
  const handleNewUserSave = () => {
    const selectedCount = dates.filter(date => date.state === 'selected-applied').length;
    
    if (selectedCount > 0) {
      // Clear any validation error
      setValidationError('');
      // Mark Step 1 complete with applied date and navigate to Step 2 (Financial Data)
      const appliedDate = formatDateDisplay(new Date());
      updateProcessState({ 
        availabilityCompleted: true,
        appliedDate: appliedDate
      });
      navigateDirectly('financial-data');
    } else {
      // Show validation error
      setValidationError('Bitte wählen Sie mindestens 1 verfügbaren Termin');
    }
  };

  const handleCancelChanges = () => {
    // Restore original selected-applied dates and clear all other selections
    setDates(prev =>
      prev.map(date => {
        if (originalSelectedAppliedDates.includes(date.id)) {
          return { ...date, state: 'selected-applied' };
        } else if (date.state === 'booked') {
          return date; // Keep booked dates unchanged
        } else {
          return { ...date, state: 'default' };
        }
      })
    );
    setMode('display');
  };

  const getDateButtonStyles = (state: string, isClickable: boolean) => {
    const baseStyles = 'transition-colors duration-200';
    const interactiveStyles = isClickable ? 'cursor-pointer hover:opacity-90' : 'cursor-default';
    
    switch (state) {
      case 'selected-applied':
        return `bg-[var(--ucm-light-blue)] border-[var(--ucm-light-blue)] text-[var(--ucm-dark-blue)] ${baseStyles} ${interactiveStyles}`;
      case 'booked':
        return `bg-[var(--ucm-success-green)] border-[var(--ucm-success-green)] text-white ${baseStyles} cursor-default`;
      default:
        const disabledStyles = (!canSelectMore && mode === 'edit') ? 'opacity-50 cursor-not-allowed' : interactiveStyles;
        return `bg-white border-[var(--ucm-border-light)] border hover:bg-gray-50 ${baseStyles} ${disabledStyles}`;
    }
  };

  const getDateButtonContent = (dateOption: DateOption) => {
    if (dateOption.state === 'selected-applied') {
      // Dynamic text based on mode
      const displayText = mode === 'edit' ? 'Selected' : 'Applied';
      
      return (
        <div className="flex items-center justify-between w-full">
          <div className="text-left">
            <div className="font-semibold text-[var(--ucm-dark-blue)]">
              {dateOption.displayDate}
            </div>
            <div className="text-sm mt-1 text-[var(--ucm-dark-blue)]">
              {displayText}
            </div>
          </div>
        </div>
      );
    }

    if (dateOption.state === 'booked') {
      return (
        <div className="flex items-center justify-between w-full">
          <div className="text-left">
            <div className="font-semibold text-white">
              {dateOption.displayDate}
            </div>
            <div className="text-sm mt-1 text-white">
              Booked
            </div>
          </div>
          <Check size={20} className="text-white" />
        </div>
      );
    }

    // Default state
    return (
      <div className="flex items-center justify-between w-full">
        <div className="text-left">
          <div className="font-semibold text-[var(--ucm-text-dark)]">
            {dateOption.displayDate}
          </div>
          <div className="text-sm flex items-center mt-1 text-[var(--ucm-text-medium)]">
            <User size={12} className="mr-1" />
            {dateOption.capacity}
          </div>
        </div>
      </div>
    );
  };

  const isDateClickable = (dateOption: DateOption) => {
    if (isMaxWorkLimitReached) return false;
    if (dateOption.state === 'booked') return false;
    
    // For new signup users, always allow clicking in display mode
    if (userType === 'new_signup' && mode === 'display') {
      if (dateOption.state === 'default' && !canSelectMore) return false;
      return true;
    }
    
    // For existing users, only allow clicking in edit mode
    if (mode === 'display') return false;
    if (dateOption.state === 'default' && !canSelectMore) return false;
    return true;
  };

  const jobDescription = `Bald ist es soweit, Sie können am Probebetrieb des Terminal 3 teilnehmen. Wir freuen uns auf Sie. Damit wir mit der Planung starten können, geben Sie bitte an, an welchen Tagen Sie Zeit haben.`;

  // For existing users, show booked status instead of date selection
  if (userType === 'booked_user') {
    return (
      <MainLayout>
        {/* Header Section */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[var(--ucm-text-dark)] mb-3">
            Frankfurt Airport Inspector
          </h1>
          
          {/* Location Card Component */}
          <div className="flex items-center mb-3">
            <div className="flex items-center bg-[var(--ucm-light-blue)] rounded-full w-8 h-8 justify-center mr-2">
              <MapPin size={16} className="text-white" />
            </div>
            <span className="text-[var(--ucm-text-dark)] font-medium">
              Terminal 3 - Security Checkpoint
            </span>
          </div>
        </div>

        {/* Booked Status */}
        <div className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)] mb-6">
          <h2 className="text-xl font-semibold text-[var(--ucm-text-dark)] mb-4">{t('apply.bookingStatus.title')}</h2>
          <div className="bg-[var(--ucm-success-green)] text-white rounded-2xl p-4 mb-4">
            <div className="flex items-center">
              <Check size={20} className="mr-2" />
              <span className="font-semibold">{t('apply.bookingStatus.bookedShifts', { count: jobs.length })}</span>
            </div>
          </div>
          
          {/* Show booked shifts */}
          <div className="space-y-3">
            {jobs.map((job, index) => (
              <div key={job.id} className="border border-[var(--ucm-border-light)] rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-[var(--ucm-text-dark)]">{t('apply.bookingStatus.shift', { number: index + 1 })}</span>
                  <span className="text-sm text-[var(--ucm-success-green)] font-medium">{t('apply.bookingStatus.booked')}</span>
                </div>
                <div className="flex items-center space-x-4 text-sm text-[var(--ucm-text-medium)]">
                  <div className="flex items-center">
                    <Calendar size={16} className="mr-1" />
                    {formatDateDisplay(job.date)}
                  </div>
                  <span>{job.time}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Edit Option */}
        <div className="bg-[var(--ucm-light-blue)] border border-[var(--ucm-dark-blue)] rounded-2xl p-4 mb-6">
          <h3 className="font-semibold text-[var(--ucm-dark-blue)] mb-2">{t('apply.changeAvailability.title')}</h3>
          <p className="text-sm text-[var(--ucm-dark-blue)] mb-3">
            {t('apply.changeAvailability.description')}
          </p>
          <button className="w-full h-10 bg-white border border-[var(--ucm-dark-blue)] text-[var(--ucm-dark-blue)] font-medium rounded-full hover:bg-gray-50 transition-colors duration-200">
            {t('apply.changeAvailability.contactButton')}
          </button>
        </div>
      </MainLayout>
    );
  }

  const renderBottomActions = () => {
    if (isMaxWorkLimitReached) {
      return (
        <div className="text-center py-4">
          <p className="text-[var(--ucm-text-medium)] text-sm">
            Maximum work limit reached. All applications withdrawn.
          </p>
        </div>
      );
    }

    if (mode === 'display') {
      const hasAnySelectedAppliedDates = selectedAppliedCount > 0;
      
      // For new signup users, use direct save without edit mode
      if (userType === 'new_signup') {
        return (
          <button
            onClick={handleNewUserSave}
            disabled={totalSelectedCount === 0}
            className={`w-full h-12 font-semibold rounded-full transition-colors duration-200 ${
              totalSelectedCount === 0
                ? 'bg-[var(--ucm-disabled-gray)] text-[var(--ucm-text-medium)] cursor-not-allowed'
                : 'bg-[var(--ucm-primary-teal)] text-white hover:bg-[var(--ucm-primary-teal-hover)]'
            }`}
          >
            {totalSelectedCount === 0 ? 'Set Availability' : 'Set Availability & Continue'}
          </button>
        );
      }

      // For existing users, use edit mode
      return (
        <button
          onClick={handleEnterEditMode}
          className="w-full h-12 bg-[var(--ucm-primary-teal)] text-white font-semibold rounded-full hover:bg-[var(--ucm-primary-teal-hover)] transition-colors duration-200"
        >
          {getButtonText()}
        </button>
      );
    }

    return (
      <div className="space-y-3">
        <button
          onClick={handleSaveUpdate}
          className="w-full h-12 bg-[var(--ucm-primary-teal)] text-white font-semibold rounded-full hover:bg-[var(--ucm-primary-teal-hover)] transition-colors duration-200"
        >
          Set Availability &amp; Continue
        </button>
        <button
          onClick={handleCancelChanges}
          className="w-full h-12 bg-white border-2 border-[var(--ucm-info-blue)] text-[var(--ucm-info-blue)] font-medium rounded-full hover:bg-blue-50 transition-colors duration-200"
        >
          Cancel changes
        </button>
      </div>
    );
  };

  return (
    <MainLayout>
      {/* Header Section */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[var(--ucm-text-dark)] mb-3">
          Frankfurt Airport Inspector
        </h1>
        
        {/* Location Card Component */}
        <div className="flex items-center mb-3">
          <div className="flex items-center bg-[var(--ucm-light-blue)] rounded-full w-8 h-8 justify-center mr-2">
            <MapPin size={16} className="text-white" />
          </div>
          <span className="text-[var(--ucm-text-dark)] font-medium">
            Terminal 3 - Security Checkpoint
          </span>
        </div>
      </div>

      {/* Job Description Section */}
      <div className="mb-6">
        <p className="text-[var(--ucm-text-dark)] text-left">
          {showFullDescription ? jobDescription : `${jobDescription.substring(0, 120)}...`}
        </p>
        <button
          onClick={() => setShowFullDescription(!showFullDescription)}
          className="text-[var(--ucm-blue-link)] hover:text-[var(--ucm-blue-link-hover)] underline mt-2 transition-colors"
        >
          {showFullDescription ? 'See less' : 'See more'}
        </button>
      </div>

      {/* Available Date Section with Real-time Counter */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-[var(--ucm-text-dark)] mb-2">
          Available Date ({totalSelectedCount}/{maxSelectableCount} Selectable days)
        </h2>
        <p className="text-sm text-[var(--ucm-text-medium)]">
          {userType === 'new_signup' && totalSelectedCount === 0 
            ? 'Select the dates when you\'re available to work'
            : 'All the dates available are on Monday and Friday'
          }
        </p>
        {userType === 'new_signup' && totalSelectedCount === 0 && (
          <p className="text-sm text-[var(--ucm-text-medium)] mt-1">
            You must select at least 1 date to continue
          </p>
        )}
      </div>

      {/* Date Grid */}
      <div className="grid grid-cols-2 gap-3 mb-8">
        {dates.map((dateOption) => {
          const isClickable = isDateClickable(dateOption);
          
          return (
            <button
              key={dateOption.id}
              onClick={() => handleDateClick(dateOption.id)}
              disabled={!isClickable}
              className={`p-4 rounded-2xl ${getDateButtonStyles(dateOption.state, isClickable)}`}
            >
              {getDateButtonContent(dateOption)}
            </button>
          );
        })}
      </div>

      {/* Validation Error */}
      {validationError && (
        <div className="bg-[var(--ucm-error-red)] bg-opacity-10 border border-[var(--ucm-error-red)] rounded-xl p-3 mb-4">
          <p className="text-[var(--ucm-error-red)] text-sm text-center">{validationError}</p>
        </div>
      )}

      {/* Bottom Actions */}
      <div className="space-y-3">
        {renderBottomActions()}
      </div>
    </MainLayout>
  );
}